import { FormField, Inline, Paragraph, RadioInputGroup } from '@george-labs.com/design-system';
import type { ItemPropTypes } from '@george-labs.com/design-system/types/components/Dropdown';
import type { FORM_FIELD_LABEL_POSITION } from '@george-labs.com/design-system/types/components/Form';
import type { RadioInputGroupProps } from '@george-labs.com/design-system/types/components/Input';
import type { ChangeEvent, ComponentProps } from 'react';

import { useField } from '@luna/form2';

import { shouldShowError } from './FieldErrorVisibilityService';
import type { FormFieldProps } from './types/FormFieldProps';

export type FormRadioInputGroupFieldItem = { 'data-cy'?: string } & ItemPropTypes;

interface Props<FormValues> extends FormFieldProps<FormValues>, RadioInputGroupProps {
    items: FormRadioInputGroupFieldItem[];
    selectedItemsText?: string;
    inputProps?: Partial<ComponentProps<typeof RadioInputGroup>>;
    className?: string;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

interface FormRadioInputGroupFieldStatics {
    LABEL_POSITION: typeof FORM_FIELD_LABEL_POSITION;
}

const LABEL_COLUMN_WIDTH = 4;

export function FormRadioInputGroupField<FormValues = void>({
    'data-cy': dataCy,
    fieldName,
    items,
    label,
    labelPosition,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    required = false,
    disabled = false,
    description,
    inputProps,
    className,
    isErrorVisible = true,
    warning,
    info,
    alert,
    addon,
    shouldClearOnUnmount,
    showErrorBeforeBlur,
    compact,
}: Props<FormValues>) {
    const [{ value, onChange }, { error, touched, dirty }, { setTouched }] = useField<string>(
        fieldName,
        { shouldClearOnUnmount },
    );

    const isErrorMessageVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event);
        setTouched();
        inputProps?.onChange?.(event);
    };

    return (
        <FormField
            description={description}
            label={label && <Inline data-cy={`${dataCy}-label`}>{label}</Inline>}
            labelPosition={labelPosition}
            labelColumnWidth={labelColumnWidth}
            required={required}
            disabled={disabled}
            error={
                isErrorMessageVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            className={className}
            name={fieldName}
            addon={addon}
            warning={warning}
            info={info}
            alert={alert}
            input={RadioInputGroup}
            inputProps={{
                'data-cy': dataCy,
                value,
                items,
                onChange: handleChange,
            }}
            compact={compact}
        />
    );
}

export type FormRadioInputGroupField = typeof FormRadioInputGroupField &
    FormRadioInputGroupFieldStatics;

FormRadioInputGroupField.LABEL_POSITION = FormField.LABEL_POSITION;
